import { getStoredPairs } from './pairing';
import type { LoginPath, SignatureRequirement } from '../types/identity';
import type { LoginProof } from '../types/identity';

/**
 * Build allowed pubkeys for strict verification: from requirements (cle_publique)
 * and from pairs (pair.publicKey, or identityKey for local pair).
 */
export function buildAllowedPubkeys(
  loginPath: LoginPath,
  identityPublicKey: string,
): Set<string> {
  const out = new Set<string>();
  const pairs = getStoredPairs();
  const pairByUuid = new Map(pairs.map((p) => [p.uuid, p]));
  const pairsSet = new Set(loginPath.pairs_attendus);

  for (const req of loginPath.signatures_requises) {
    if (req.cle_publique !== undefined) {
      out.add(req.cle_publique);
    }
    if (req.pair_uuid === undefined) {
      continue;
    }
    const pair = pairByUuid.get(req.pair_uuid);
    if (pair === undefined || !pairsSet.has(req.pair_uuid)) {
      continue;
    }
    if (pair.is_local) {
      out.add(identityPublicKey);
    } else if (pair.publicKey !== undefined) {
      out.add(pair.publicKey);
    }
  }
  return out;
}

/**
 * Required signature count per member (max of cardinalite_minimale over requirements).
 */
export function requiredSigsPerMember(
  requirements: SignatureRequirement[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of requirements) {
    const c = r.cardinalite_minimale ?? 1;
    const cur = m.get(r.membre_uuid) ?? 0;
    m.set(r.membre_uuid, Math.max(cur, c));
  }
  return m;
}

/**
 * Build pairsPerMember (member -> set of pair_uuids) from signatures and pairToMembers.
 */
function pairsPerMemberFromSigs(
  signatures: Array<{ pair_uuid: string }>,
  pairToMembers: Map<string, string[]>,
): Map<string, Set<string>> {
  const pairsPerMember = new Map<string, Set<string>>();
  for (const sig of signatures) {
    const members = pairToMembers.get(sig.pair_uuid);
    if (members === undefined) {
      continue;
    }
    for (const memb of members) {
      let set = pairsPerMember.get(memb);
      if (set === undefined) {
        set = new Set<string>();
        pairsPerMember.set(memb, set);
      }
      set.add(sig.pair_uuid);
    }
  }
  return pairsPerMember;
}

/**
 * Check we have enough distinct pairs per member (for collecte distante).
 * Count distinct pair_uuids per member via pairToMembers.
 */
export function hasEnoughSignatures(
  path: LoginPath,
  signatures: Array<{ pair_uuid: string }>,
  pairToMembers: Map<string, string[]>,
): boolean {
  const required = requiredSigsPerMember(path.signatures_requises);
  const pairsPerMember = pairsPerMemberFromSigs(signatures, pairToMembers);
  for (const [member, need] of required) {
    const have = pairsPerMember.get(member)?.size ?? 0;
    if (have < need) {
      return false;
    }
  }
  return true;
}

/**
 * Progress of signature collect: satisfied vs required (for notifications/UI).
 * satisfied = sum over members of min(have, need); required = sum of need.
 */
export function collectProgress(
  path: LoginPath,
  signatures: Array<{ pair_uuid: string }>,
  pairToMembers: Map<string, string[]>,
): { satisfied: number; required: number } {
  const required = requiredSigsPerMember(path.signatures_requises);
  const pairsPerMember = pairsPerMemberFromSigs(signatures, pairToMembers);
  let satisfied = 0;
  let requiredTotal = 0;
  for (const [member, need] of required) {
    requiredTotal += need;
    const have = pairsPerMember.get(member)?.size ?? 0;
    satisfied += Math.min(have, need);
  }
  return { satisfied, required: requiredTotal };
}

/**
 * True if any signature comes from a non-local pair (2nd device).
 * Used to require manual accept before validating when words may have been intercepted.
 */
export function hasRemoteSignatures(
  signatures: Array<{ pair_uuid: string }>,
  pairsAttendus: string[],
): boolean {
  const pairs = getStoredPairs();
  const set = new Set(pairsAttendus);
  const pairByUuid = new Map(pairs.map((p) => [p.uuid, p]));
  for (const sig of signatures) {
    if (!set.has(sig.pair_uuid)) {
      continue;
    }
    const pair = pairByUuid.get(sig.pair_uuid);
    if (pair !== undefined && !pair.is_local) {
      return true;
    }
  }
  return false;
}

/**
 * Members that have at least one signature in proof (via pair → membres_parents_uuid).
 */
function membersWithSignatures(proof: LoginProof): Set<string> {
  const pairs = getStoredPairs();
  const pairByUuid = new Map(pairs.map((p) => [p.uuid, p]));
  const out = new Set<string>();
  for (const sig of proof.signatures) {
    const pair = pairByUuid.get(sig.pair_uuid);
    if (pair === undefined) {
      continue;
    }
    for (const m of pair.membres_parents_uuid) {
      out.add(m);
    }
  }
  return out;
}

/**
 * Check that dependencies between requirements are satisfied.
 * For each requirement with dependances (member UUIDs), those members must have
 * at least one signature in the proof (via a pair of that member).
 */
export function checkDependenciesSatisfied(
  path: LoginPath,
  proof: LoginProof,
): boolean {
  const membersSigned = membersWithSignatures(proof);
  for (const req of path.signatures_requises) {
    const deps = req.dependances;
    if (deps === undefined || deps.length === 0) {
      continue;
    }
    for (const dep of deps) {
      if (!membersSigned.has(dep)) {
        return false;
      }
    }
  }
  return true;
}
